import { Dialog } from '@headlessui/react';
import { PropsWithChildren, ReactNode, Suspense } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Loading from './loading.tsx';
import { cn } from '../../utils/common-utils.ts';

type IModal = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  aboveTitle?: string;
  titleContent?: ReactNode;
  centerContent?: ReactNode;
  headerActions?: ReactNode;
  description?: string;
  fullScreen?: boolean;
  panelClassName?: string;
  bodyClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  titleRowClassName?: string;
  overlayClassName?: string;
  containerClassName?: string;
}>;

const Modal = ({
  isOpen,
  onClose,
  title,
  titleContent,
  centerContent,
  headerActions,
  children,
  description,
  fullScreen = false,
  aboveTitle,
  panelClassName,
  bodyClassName,
  headerClassName,
  titleClassName,
  titleRowClassName,
  overlayClassName,
  containerClassName,
}: IModal) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div
        className={cn(
          'fixed inset-0 bg-black/40 backdrop-blur-sm',
          overlayClassName,
        )}
        aria-hidden="true"
      />
      <div
        className={cn(
          'fixed inset-0 flex w-screen items-center justify-center p-4',
          containerClassName,
        )}
      >
        <Dialog.Panel
          className={cn(
            'mx-auto max-h-full overflow-y-auto rounded-lg bg-white p-4',
            fullScreen && 'h-full w-full',
            panelClassName,
          )}
        >
          <Dialog.Title
            className={cn(
              'relative my-1 flex items-center justify-between px-1 font-semibold',
              headerClassName,
            )}
          >
            <div className={'relative z-10 flex flex-col'}>
              {aboveTitle && (
                <div className={'flex flex-row items-center gap-4'}>
                  <span>{aboveTitle}</span>
                </div>
              )}
              <div
                className={cn(
                  'flex flex-row items-center gap-4',
                  titleRowClassName,
                )}
              >
                <span className={titleClassName}>{title}</span>
                {titleContent}
              </div>
            </div>
            {centerContent && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="pointer-events-auto">{centerContent}</div>
              </div>
            )}
            <div className="relative z-10 ml-4 flex items-center gap-1">
              {headerActions}
              <button onClick={onClose}>
                <XMarkIcon
                  className={'h-8 w-8 rounded p-1 text-black hover:bg-light-blue'}
                />
              </button>
            </div>
          </Dialog.Title>
          <Dialog.Description className={'description my-1 px-1'}>
            {description}
          </Dialog.Description>
          <hr className={'opacity-10'} />
          <Suspense
            fallback={
              <Loading className={'m-4 mt-10 min-w-32 p-4 lg:min-w-72'} />
            }
          >
            <div className={bodyClassName}>{children}</div>
          </Suspense>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
export default Modal;
